/**
 * ISO-4217 Currency List (Frontend)
 * 
 * Complete list of all ISO-4217 currency codes with names, symbols, and default decimal places.
 * This matches the backend list for consistency.
 * 
 * Exchange rates are fetched dynamically from the API, so only metadata is hardcoded here.
 */

export interface ISO4217Currency {
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number; // Default decimal places (0-4)
}

export const ISO4217_CURRENCIES: readonly ISO4217Currency[] = [
  // Major currencies (0-2 decimals)
  { code: "USD", name: "US Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "EUR", name: "Euro", symbol: "€", decimalPlaces: 2 },
  { code: "GBP", name: "British Pound", symbol: "£", decimalPlaces: 2 },
  { code: "JPY", name: "Japanese Yen", symbol: "¥", decimalPlaces: 0 },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥", decimalPlaces: 2 },
  { code: "INR", name: "Indian Rupee", symbol: "₹", decimalPlaces: 2 },
  { code: "AMD", name: "Armenian Dram", symbol: "֏", decimalPlaces: 0 },
  { code: "RUB", name: "Russian Ruble", symbol: "₽", decimalPlaces: 2 },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$", decimalPlaces: 2 },
  { code: "AUD", name: "Australian Dollar", symbol: "A$", decimalPlaces: 2 },
  { code: "CHF", name: "Swiss Franc", symbol: "CHF", decimalPlaces: 2 },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$", decimalPlaces: 2 },
  
  // A
  { code: "AED", name: "UAE Dirham", symbol: "د.إ", decimalPlaces: 2 },
  { code: "AFN", name: "Afghan Afghani", symbol: "؋", decimalPlaces: 0 },
  { code: "ALL", name: "Albanian Lek", symbol: "L", decimalPlaces: 2 },
  { code: "AOA", name: "Angolan Kwanza", symbol: "Kz", decimalPlaces: 2 },
  { code: "ARS", name: "Argentine Peso", symbol: "$", decimalPlaces: 2 },
  { code: "AWG", name: "Aruban Florin", symbol: "ƒ", decimalPlaces: 2 },
  { code: "AZN", name: "Azerbaijani Manat", symbol: "₼", decimalPlaces: 2 },
  
  // B
  { code: "BAM", name: "Bosnia-Herzegovina Convertible Mark", symbol: "КМ", decimalPlaces: 2 },
  { code: "BBD", name: "Barbadian Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "BDT", name: "Bangladeshi Taka", symbol: "৳", decimalPlaces: 2 },
  { code: "BGN", name: "Bulgarian Lev", symbol: "лв", decimalPlaces: 2 },
  { code: "BHD", name: "Bahraini Dinar", symbol: ".د.ب", decimalPlaces: 3 },
  { code: "BIF", name: "Burundian Franc", symbol: "Fr", decimalPlaces: 0 },
  { code: "BMD", name: "Bermudan Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "BND", name: "Brunei Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "BOB", name: "Bolivian Boliviano", symbol: "Bs.", decimalPlaces: 2 },
  { code: "BRL", name: "Brazilian Real", symbol: "R$", decimalPlaces: 2 },
  { code: "BSD", name: "Bahamian Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "BTN", name: "Bhutanese Ngultrum", symbol: "Nu.", decimalPlaces: 2 },
  { code: "BWP", name: "Botswanan Pula", symbol: "P", decimalPlaces: 2 },
  { code: "BYN", name: "Belarusian Ruble", symbol: "Br", decimalPlaces: 2 },
  { code: "BZD", name: "Belize Dollar", symbol: "$", decimalPlaces: 2 },
  
  // C
  { code: "CDF", name: "Congolese Franc", symbol: "Fr", decimalPlaces: 2 },
  { code: "CLF", name: "Chilean Unit of Account (UF)", symbol: "UF", decimalPlaces: 4 },
  { code: "CLP", name: "Chilean Peso", symbol: "$", decimalPlaces: 0 },
  { code: "COP", name: "Colombian Peso", symbol: "$", decimalPlaces: 2 },
  { code: "CRC", name: "Costa Rican Colón", symbol: "₡", decimalPlaces: 2 },
  { code: "CUP", name: "Cuban Peso", symbol: "$", decimalPlaces: 2 },
  { code: "CVE", name: "Cape Verdean Escudo", symbol: "Esc", decimalPlaces: 2 },
  { code: "CZK", name: "Czech Koruna", symbol: "Kč", decimalPlaces: 2 },
  
  // D
  { code: "DJF", name: "Djiboutian Franc", symbol: "Fr", decimalPlaces: 0 },
  { code: "DKK", name: "Danish Krone", symbol: "kr", decimalPlaces: 2 },
  { code: "DOP", name: "Dominican Peso", symbol: "$", decimalPlaces: 2 },
  { code: "DZD", name: "Algerian Dinar", symbol: "د.ج", decimalPlaces: 2 },
  
  // E
  { code: "EGP", name: "Egyptian Pound", symbol: "£", decimalPlaces: 2 },
  { code: "ERN", name: "Eritrean Nakfa", symbol: "Nfk", decimalPlaces: 2 },
  { code: "ETB", name: "Ethiopian Birr", symbol: "Br", decimalPlaces: 2 },
  
  // F
  { code: "FJD", name: "Fijian Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "FKP", name: "Falkland Islands Pound", symbol: "£", decimalPlaces: 2 },
  
  // G
  { code: "GEL", name: "Georgian Lari", symbol: "₾", decimalPlaces: 2 },
  { code: "GHS", name: "Ghanaian Cedi", symbol: "₵", decimalPlaces: 2 },
  { code: "GIP", name: "Gibraltar Pound", symbol: "£", decimalPlaces: 2 },
  { code: "GMD", name: "Gambian Dalasi", symbol: "D", decimalPlaces: 2 },
  { code: "GNF", name: "Guinean Franc", symbol: "Fr", decimalPlaces: 0 },
  { code: "GTQ", name: "Guatemalan Quetzal", symbol: "Q", decimalPlaces: 2 },
  { code: "GYD", name: "Guyanaese Dollar", symbol: "$", decimalPlaces: 2 },
  
  // H
  { code: "HKD", name: "Hong Kong Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "HNL", name: "Honduran Lempira", symbol: "L", decimalPlaces: 2 },
  { code: "HRK", name: "Croatian Kuna", symbol: "kn", decimalPlaces: 2 },
  { code: "HTG", name: "Haitian Gourde", symbol: "G", decimalPlaces: 2 },
  { code: "HUF", name: "Hungarian Forint", symbol: "Ft", decimalPlaces: 2 },
  
  // I
  { code: "IDR", name: "Indonesian Rupiah", symbol: "Rp", decimalPlaces: 2 },
  { code: "ILS", name: "Israeli New Shekel", symbol: "₪", decimalPlaces: 2 },
  { code: "IQD", name: "Iraqi Dinar", symbol: "ع.د", decimalPlaces: 3 },
  { code: "IRR", name: "Iranian Rial", symbol: "﷼", decimalPlaces: 2 },
  { code: "ISK", name: "Icelandic Króna", symbol: "kr", decimalPlaces: 0 },
  
  // J
  { code: "JMD", name: "Jamaican Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "JOD", name: "Jordanian Dinar", symbol: "د.ا", decimalPlaces: 3 },
  
  // K
  { code: "KES", name: "Kenyan Shilling", symbol: "Sh", decimalPlaces: 2 },
  { code: "KGS", name: "Kyrgystani Som", symbol: "с", decimalPlaces: 2 },
  { code: "KHR", name: "Cambodian Riel", symbol: "៛", decimalPlaces: 2 },
  { code: "KMF", name: "Comorian Franc", symbol: "Fr", decimalPlaces: 0 },
  { code: "KPW", name: "North Korean Won", symbol: "₩", decimalPlaces: 2 },
  { code: "KRW", name: "South Korean Won", symbol: "₩", decimalPlaces: 0 },
  { code: "KWD", name: "Kuwaiti Dinar", symbol: "د.ك", decimalPlaces: 3 },
  { code: "KYD", name: "Cayman Islands Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "KZT", name: "Kazakhstani Tenge", symbol: "₸", decimalPlaces: 2 },
  
  // L
  { code: "LAK", name: "Laotian Kip", symbol: "₭", decimalPlaces: 2 },
  { code: "LBP", name: "Lebanese Pound", symbol: "£", decimalPlaces: 2 },
  { code: "LKR", name: "Sri Lankan Rupee", symbol: "Rs", decimalPlaces: 2 },
  { code: "LRD", name: "Liberian Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "LSL", name: "Lesotho Loti", symbol: "L", decimalPlaces: 2 },
  { code: "LYD", name: "Libyan Dinar", symbol: "ل.د", decimalPlaces: 3 },
  
  // M
  { code: "MAD", name: "Moroccan Dirham", symbol: "د.م.", decimalPlaces: 2 },
  { code: "MDL", name: "Moldovan Leu", symbol: "L", decimalPlaces: 2 },
  { code: "MGA", name: "Malagasy Ariary", symbol: "Ar", decimalPlaces: 2 },
  { code: "MKD", name: "Macedonian Denar", symbol: "ден", decimalPlaces: 2 },
  { code: "MMK", name: "Myanma Kyat", symbol: "K", decimalPlaces: 2 },
  { code: "MNT", name: "Mongolian Tugrik", symbol: "₮", decimalPlaces: 2 },
  { code: "MOP", name: "Macanese Pataca", symbol: "P", decimalPlaces: 2 },
  { code: "MRU", name: "Mauritanian Ouguiya", symbol: "UM", decimalPlaces: 2 },
  { code: "MUR", name: "Mauritian Rupee", symbol: "₨", decimalPlaces: 2 },
  { code: "MVR", name: "Maldivian Rufiyaa", symbol: "Rf", decimalPlaces: 2 },
  { code: "MWK", name: "Malawian Kwacha", symbol: "MK", decimalPlaces: 2 },
  { code: "MXN", name: "Mexican Peso", symbol: "$", decimalPlaces: 2 },
  { code: "MYR", name: "Malaysian Ringgit", symbol: "RM", decimalPlaces: 2 },
  { code: "MZN", name: "Mozambican Metical", symbol: "MT", decimalPlaces: 2 },
  
  // N
  { code: "NAD", name: "Namibian Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "NGN", name: "Nigerian Naira", symbol: "₦", decimalPlaces: 2 },
  { code: "NIO", name: "Nicaraguan Córdoba", symbol: "C$", decimalPlaces: 2 },
  { code: "NOK", name: "Norwegian Krone", symbol: "kr", decimalPlaces: 2 },
  { code: "NPR", name: "Nepalese Rupee", symbol: "₨", decimalPlaces: 2 },
  { code: "NZD", name: "New Zealand Dollar", symbol: "$", decimalPlaces: 2 },
  
  // O
  { code: "OMR", name: "Omani Rial", symbol: "ر.ع.", decimalPlaces: 3 },
  
  // P
  { code: "PAB", name: "Panamanian Balboa", symbol: "B/.", decimalPlaces: 2 },
  { code: "PEN", name: "Peruvian Nuevo Sol", symbol: "S/.", decimalPlaces: 2 },
  { code: "PGK", name: "Papua New Guinean Kina", symbol: "K", decimalPlaces: 2 },
  { code: "PHP", name: "Philippine Peso", symbol: "₱", decimalPlaces: 2 },
  { code: "PKR", name: "Pakistani Rupee", symbol: "₨", decimalPlaces: 2 },
  { code: "PLN", name: "Polish Zloty", symbol: "zł", decimalPlaces: 2 },
  { code: "PYG", name: "Paraguayan Guarani", symbol: "₲", decimalPlaces: 0 },
  
  // Q
  { code: "QAR", name: "Qatari Rial", symbol: "ر.ق", decimalPlaces: 2 },
  
  // R
  { code: "RON", name: "Romanian Leu", symbol: "lei", decimalPlaces: 2 },
  { code: "RSD", name: "Serbian Dinar", symbol: "дин", decimalPlaces: 2 },
  { code: "RWF", name: "Rwandan Franc", symbol: "Fr", decimalPlaces: 0 },
  
  // S
  { code: "SAR", name: "Saudi Riyal", symbol: "ر.س", decimalPlaces: 2 },
  { code: "SBD", name: "Solomon Islands Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "SCR", name: "Seychellois Rupee", symbol: "₨", decimalPlaces: 2 },
  { code: "SDG", name: "Sudanese Pound", symbol: "£", decimalPlaces: 2 },
  { code: "SEK", name: "Swedish Krona", symbol: "kr", decimalPlaces: 2 },
  { code: "SHP", name: "Saint Helena Pound", symbol: "£", decimalPlaces: 2 },
  { code: "SLE", name: "Sierra Leonean Leone", symbol: "Le", decimalPlaces: 2 },
  { code: "SLL", name: "Sierra Leonean Leone (old)", symbol: "Le", decimalPlaces: 2 },
  { code: "SOS", name: "Somali Shilling", symbol: "Sh", decimalPlaces: 2 },
  { code: "SRD", name: "Surinamese Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "SSP", name: "South Sudanese Pound", symbol: "£", decimalPlaces: 2 },
  { code: "STN", name: "São Tomé and Príncipe Dobra", symbol: "Db", decimalPlaces: 2 },
  { code: "SYP", name: "Syrian Pound", symbol: "£", decimalPlaces: 2 },
  { code: "SZL", name: "Swazi Lilangeni", symbol: "L", decimalPlaces: 2 },
  
  // T
  { code: "THB", name: "Thai Baht", symbol: "฿", decimalPlaces: 2 },
  { code: "TJS", name: "Tajikistani Somoni", symbol: "ЅМ", decimalPlaces: 2 },
  { code: "TMT", name: "Turkmenistani Manat", symbol: "m", decimalPlaces: 2 },
  { code: "TND", name: "Tunisian Dinar", symbol: "د.ت", decimalPlaces: 3 },
  { code: "TOP", name: "Tongan Paʻanga", symbol: "T$", decimalPlaces: 2 },
  { code: "TRY", name: "Turkish Lira", symbol: "₺", decimalPlaces: 2 },
  { code: "TTD", name: "Trinidad and Tobago Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "TVD", name: "Tuvaluan Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "TWD", name: "New Taiwan Dollar", symbol: "NT$", decimalPlaces: 2 },
  { code: "TZS", name: "Tanzanian Shilling", symbol: "Sh", decimalPlaces: 2 },
  
  // U
  { code: "UAH", name: "Ukrainian Hryvnia", symbol: "₴", decimalPlaces: 2 },
  { code: "UGX", name: "Ugandan Shilling", symbol: "Sh", decimalPlaces: 0 },
  { code: "UYU", name: "Uruguayan Peso", symbol: "$", decimalPlaces: 2 },
  { code: "UZS", name: "Uzbekistan Som", symbol: "лв", decimalPlaces: 2 },
  
  // V
  { code: "VES", name: "Venezuelan Bolívar Soberano", symbol: "Bs.S.", decimalPlaces: 2 },
  { code: "VND", name: "Vietnamese Dong", symbol: "₫", decimalPlaces: 0 },
  { code: "VUV", name: "Vanuatu Vatu", symbol: "Vt", decimalPlaces: 0 },
  
  // W
  { code: "WST", name: "Samoan Tala", symbol: "T", decimalPlaces: 2 },
  
  // X (Special purpose currencies)
  { code: "XAF", name: "CFA Franc BEAC", symbol: "Fr", decimalPlaces: 0 },
  { code: "XCD", name: "East Caribbean Dollar", symbol: "$", decimalPlaces: 2 },
  { code: "XOF", name: "CFA Franc BCEAO", symbol: "Fr", decimalPlaces: 0 },
  { code: "XPF", name: "CFP Franc", symbol: "Fr", decimalPlaces: 0 },
  
  // Y
  { code: "YER", name: "Yemeni Rial", symbol: "﷼", decimalPlaces: 2 },
  
  // Z
  { code: "ZAR", name: "South African Rand", symbol: "R", decimalPlaces: 2 },
  { code: "ZMW", name: "Zambian Kwacha", symbol: "ZK", decimalPlaces: 2 },
  { code: "ZWL", name: "Zimbabwean Dollar", symbol: "$", decimalPlaces: 2 },
] as const;

/**
 * Get currency info by code
 */
export const getCurrencyByCode = (code: string): ISO4217Currency | undefined => {
  return ISO4217_CURRENCIES.find((c) => c.code === code);
};

/**
 * Check if a currency code is valid ISO-4217
 */
export const isValidISO4217Code = (code: string): boolean => {
  return ISO4217_CURRENCIES.some((c) => c.code === code);
};

/**
 * Get all currency codes
 */
export const getAllCurrencyCodes = (): string[] => {
  return ISO4217_CURRENCIES.map((c) => c.code);
};
